// 事件工作台 — 事件统计 + 告警/消息入口 + 最近消息流
// 接口（reverse engineered from CMessageHttpImpl + HttpUrlBuilder）：
//   GET  message-service /user/message/cursor   ?cursor=&count=&title=<type>   av=4
//        → data.items: MessageItemInfo[]，data.next_cursor: string
//   POST message-service /user/message/updateState   {message_id,sub_type,widget_id}   av=2
import { Alert, App as AntdApp, Button, Drawer, Empty, Spin, Tag, Typography } from 'antd';
import {
	AlertOutlined,
	BellOutlined,
	HolderOutlined,
	MailOutlined,
	MinusCircleOutlined,
	PlusCircleOutlined,
	ReloadOutlined,
	SettingOutlined,
} from '@ant-design/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { callInternal } from '../api/internal.js';
import { colors } from '../theme/colors.js';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { capabilitiesForArea } from '../capabilities.js';
import { useRegion } from '../hooks/useRegion.js';
import { formatNumber, marketTimeZone, normalizeLocale } from '../utils/locale-format.js';
import {
	type ActionTileItem,
	ActionTileGrid,
	MobileRootPage,
	RegionTag,
	RootHeader,
	SectionBlock,
	SummaryStrip,
} from '../components/MobileRootScaffold.js';

/** 反编译 com.ulucu.model.thridpart.http.manager.message.entity.MessageItemInfo */
interface MessageItemInfo {
	message_id: string;
	type: string;
	title: string;          // 类型显示名（"AI 视频" / "事件" 等，由后端给出）
	text: string;           // 富文本（含 <a> 标签）
	format_text?: string;   // 格式化纯文本备用
	create_time: string;
	current_cursor?: string;
	next_cursor?: string;
	stat: string;           // "0" = 未读
	unread_count?: string;
	images?: string[];
	widget_id?: string;
	// 各类型携带的额外字段（key_person / pos_trade 等）
	relation?: {
		// 重点人员到店 import_user
		arrive_imgurl?: string;
		arrive_time?: string;
		arrive_num?: string;
		realname?: string;
		sex?: string;             // "0" = 女, "1" = 男
		mark?: string;
		// pos_trade 悠掌柜
		trade_time?: string;
		img_url?: string;
		title?: string;           // "前缀：值1;值2" 格式
		type?: string;            // "1"-"15" 数字类别
		// 共用
		group_name?: string;
		store_name?: string;
		[k: string]: unknown;
	};
}
interface CursorResp {
	items?: MessageItemInfo[];
	next_cursor?: string;
}
interface UnreadItem {
	title?: string;
	type?: string;
	unread_count?: string | number;
	[k: string]: unknown;
}
interface EventCounts {
	my?: string | number;
	wait?: string | number;
	all?: string | number;
	destroy?: string | number;
	wait_handle?: string | number;
	my_appeal?: string | number;
	appeal_audit?: string | number;
	recheck?: string | number;
	[k: string]: unknown;
}

/** 反编译 com.ulucu.model.thridpart.utils.IntentAction.VALUE.TYPE_*  */
interface MsgType {
	key: string;          // type 字符串（API 用）
	i18nKey: string;      // i18n key
}
const ALL_TYPES: MsgType[] = [
	{ key: 'system',                i18nKey: 'msg_t.system' },
	{ key: 'event',                 i18nKey: 'msg_t.event' },
	{ key: 'examine',               i18nKey: 'msg_t.examine' },
	{ key: 'import_user',           i18nKey: 'msg_t.key_person' },
	{ key: 'black_list',            i18nKey: 'msg_t.blacklist' },
	{ key: 'shop_inspection',       i18nKey: 'msg_t.xundian' },
	{ key: 'detachment_detection',  i18nKey: 'msg_t.tuogang' },
	{ key: 'youxun_picai',          i18nKey: 'msg_t.pic_ai' },
	{ key: 'youxun_moveai',         i18nKey: 'msg_t.move_ai' },
	{ key: 'face_mask',             i18nKey: 'msg_t.face_mask' },
	{ key: 'data_warning',          i18nKey: 'msg_t.data_warn' },
	{ key: 'certificate_exceed',    i18nKey: 'msg_t.cert' },
	{ key: 'youschool',             i18nKey: 'msg_t.youschool' },
	{ key: 'store_build',           i18nKey: 'msg_t.store_build' },
	{ key: 'deivce_abnormal',       i18nKey: 'msg_t.image_exception' },
	{ key: 'alarm',                 i18nKey: 'msg_t.alarm' },
	{ key: 'human_alarm',           i18nKey: 'msg_t.human_alarm' },
	{ key: 'face',                  i18nKey: 'msg_t.face' },
	{ key: 'aitagvideo',            i18nKey: 'msg_t.ai_tag_video' },
	{ key: 'pos_trade',             i18nKey: 'msg_t.pos_trade' },
	{ key: 'spot_inspection',       i18nKey: 'msg_t.dianjian' },
];
const TYPE_BY_KEY = new Map(ALL_TYPES.map((t) => [t.key, t]));
const APAC_MESSAGE_TYPES = new Set([
	'system',
	'event',
	'shop_inspection',
	'youxun_picai',
	'youxun_moveai',
	'alarm',
	'aitagvideo',
	'pos_trade',
	'spot_inspection',
]);

const DEFAULT_ORDER = ALL_TYPES.map((t) => t.key);
const STORAGE_KEY = 'ulucu-message-order';

const PRIMARY = colors.primary.bg;
const PRIMARY_TEXT = colors.primary.text;          // 4.59:1 on white ✓

function loadOrder(): string[] {
	try {
		const raw = localStorage.getItem(STORAGE_KEY);
		if (!raw) return DEFAULT_ORDER;
		const arr = JSON.parse(raw) as string[];
		const valid = arr.filter((k) => TYPE_BY_KEY.has(k));
		return valid.length > 0 ? valid : DEFAULT_ORDER;
	} catch {
		return DEFAULT_ORDER;
	}
}

const PAGE_SIZE = 20;

function countValue(value: unknown): number {
	const n = Number(value ?? 0);
	return Number.isFinite(n) ? n : 0;
}

function normalizeUnreadData(data: unknown): UnreadItem[] {
	if (Array.isArray(data)) return data as UnreadItem[];
	if (data && typeof data === 'object' && Array.isArray((data as { items?: unknown }).items)) {
		return (data as { items: UnreadItem[] }).items;
	}
	return [];
}

function dayBoundsQuery(locale: string) {
	const parts = new Intl.DateTimeFormat('en-CA', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit',
		timeZone: marketTimeZone(locale),
	}).formatToParts(new Date());
	const value = (type: string) => parts.find((part) => part.type === type)?.value ?? '';
	const y = value('year');
	const m = value('month');
	const d = value('day');
	return {
		start_time: `${y}-${m}-${d} 00:00:00`,
		end_time: `${y}-${m}-${d} 23:59:59`,
	};
}

function formatIncidentTime(value: Date, locale: string): string {
	return new Intl.DateTimeFormat(normalizeLocale(locale), {
		hour: '2-digit',
		minute: '2-digit',
		timeZone: marketTimeZone(locale),
	}).format(value);
}

export default function MessagesTab() {
	const { t, i18n } = useTranslation();
	const navigate = useNavigate();
	const { message } = AntdApp.useApp();
	const region = useRegion();
	const [order, setOrder] = useState<string[]>(loadOrder);
	const [active, setActive] = useState<string>(order[0]);
	const [items, setItems] = useState<MessageItemInfo[] | null>(null);
	const [nextCursor, setNextCursor] = useState<string>('');
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [editorOpen, setEditorOpen] = useState(false);
	const [eventCounts, setEventCounts] = useState<EventCounts | null>(null);
	const [unreadItems, setUnreadItems] = useState<UnreadItem[]>([]);
	const [overviewLoading, setOverviewLoading] = useState(false);
	const [overviewError, setOverviewError] = useState<string | null>(null);
	const [listError, setListError] = useState<string | null>(null);
	const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
	const sentinelRef = useRef<HTMLDivElement | null>(null);
	const allowedTypes = useMemo(
		() => region === 'apac' ? ALL_TYPES.filter((m) => APAC_MESSAGE_TYPES.has(m.key)) : ALL_TYPES,
		[region],
	);
	const allowedKeys = useMemo(() => new Set(allowedTypes.map((m) => m.key)), [allowedTypes]);
	const visibleOrder = useMemo(() => {
		const ordered = order.filter((key) => allowedKeys.has(key));
		const missing = allowedTypes.map((m) => m.key).filter((key) => !ordered.includes(key));
		return [...ordered, ...missing];
	}, [allowedKeys, allowedTypes, order]);
	const incidentCaps = useMemo(
		() => capabilitiesForArea('incidents', region, ['available']).filter((cap) => cap.moduleKey),
		[region],
	);
	const capByKey = useMemo(() => new Map(incidentCaps.map((cap) => [cap.key, cap])), [incidentCaps]);
	const unreadByType = useMemo(() => {
		const map = new Map<string, number>();
		for (const item of unreadItems) {
			const key = String(item.title ?? item.type ?? '');
			if (key) map.set(key, countValue(item.unread_count));
		}
		return map;
	}, [unreadItems]);
	const totalUnread = useMemo(
		() => unreadItems.reduce((sum, item) => sum + countValue(item.unread_count), 0),
		[unreadItems],
	);
	const activeUnread = unreadByType.get(active) ?? 0;
	const pendingEvents = eventCounts ? countValue(eventCounts.wait_handle ?? eventCounts.wait) : null;
	const todayEvents = eventCounts ? countValue(eventCounts.all) : null;
	const alarmUnread = unreadByType.get('alarm') ?? 0;
	const handledEvents = eventCounts ? countValue(eventCounts.destroy) : null;
	const lastUpdatedLabel = lastUpdated
		? t('root.incidents.updated_at', { time: formatIncidentTime(lastUpdated, i18n.language) })
		: t('root.incidents.not_updated');
	const metric = (value: number | null) => {
		if (overviewLoading && value == null) return '—';
		if (value == null) return '—';
		return formatNumber(value, i18n.language);
	};

	useEffect(() => {
		if (!visibleOrder.includes(active)) {
			setActive(visibleOrder[0] ?? DEFAULT_ORDER[0]);
		}
	}, [active, visibleOrder]);

	const loadOverview = useCallback(async () => {
		setOverviewLoading(true);
		setOverviewError(null);
		const query = dayBoundsQuery(i18n.language);
		const errors: string[] = [];
		try {
			const [eventResult, unreadResult] = await Promise.allSettled([
				callInternal<EventCounts>({
					short_name: 'event',
					action: '/i/event/count',
					method: 'GET',
					query,
					av: '1',
				}),
				callInternal<{ items?: UnreadItem[] } | UnreadItem[]>({
					short_name: 'message',
					action: '/user/message/unread_count',
					method: 'GET',
					av: '4',
				}),
			]);

			if (eventResult.status === 'fulfilled') {
				if (eventResult.value.code === 0) {
					setEventCounts(eventResult.value.data ?? {});
				} else {
					errors.push(`event: ${eventResult.value.msg} (code=${eventResult.value.code})`);
				}
			} else {
				errors.push(`event: ${eventResult.reason instanceof Error ? eventResult.reason.message : String(eventResult.reason)}`);
			}

			if (unreadResult.status === 'fulfilled') {
				if (unreadResult.value.code === 0) {
					setUnreadItems(normalizeUnreadData(unreadResult.value.data));
				} else {
					errors.push(`message: ${unreadResult.value.msg} (code=${unreadResult.value.code})`);
				}
			} else {
				errors.push(`message: ${unreadResult.reason instanceof Error ? unreadResult.reason.message : String(unreadResult.reason)}`);
			}

			setOverviewError(errors.length > 0 ? errors.join(' · ') : null);
			setLastUpdated(new Date());
		} finally {
			setOverviewLoading(false);
		}
	}, [i18n.language]);

	useEffect(() => {
		void loadOverview();
	}, [loadOverview, region]);

	const fetchPage = useCallback(async (cursor: string, replace: boolean) => {
		if (replace) setLoading(true); else setLoadingMore(true);
		try {
			const res = await callInternal<CursorResp | MessageItemInfo[]>({
				short_name: 'message',
				action: '/user/message/cursor',
				method: 'GET',
				query: { cursor, count: PAGE_SIZE, title: active },
				av: '4',
			});
			if (res.code !== 0) {
				if (replace) setItems([]);
				setHasMore(false);
				setListError(`${res.msg} (code=${res.code})`);
				return;
			}
			setListError(null);
			const data = res.data as unknown;
			let list: MessageItemInfo[] = [];
			let nc = '';
			if (Array.isArray(data)) {
				list = data as MessageItemInfo[];
			} else if (data && typeof data === 'object') {
				const obj = data as CursorResp;
				if (Array.isArray(obj.items)) list = obj.items;
				if (typeof obj.next_cursor === 'string') nc = obj.next_cursor;
			}
			setNextCursor(nc);
			setHasMore(Boolean(nc) && list.length > 0);
			setItems((prev) => (replace || !prev ? list : [...prev, ...list]));
		} catch (e) {
			if (replace) setItems([]);
			setHasMore(false);
			setListError(e instanceof Error ? e.message : String(e));
		} finally {
			setLoading(false);
			setLoadingMore(false);
		}
	}, [active]);

	// 切 tab → 重置 + 拉首页
	useEffect(() => {
		setItems(null);
		setNextCursor('');
		setHasMore(true);
		fetchPage('', true);
	}, [active, fetchPage]);

	// 触底加载更多 (IntersectionObserver)
	useEffect(() => {
		const el = sentinelRef.current;
		if (!el || !hasMore || loading || loadingMore) return;
		const io = new IntersectionObserver((entries) => {
			if (entries[0]?.isIntersecting && hasMore && !loadingMore && !loading) {
				fetchPage(nextCursor, false);
			}
		}, { rootMargin: '200px' });
		io.observe(el);
		return () => io.disconnect();
	}, [hasMore, loading, loadingMore, nextCursor, fetchPage]);

	const markRead = useCallback(async (m: MessageItemInfo) => {
		// 已读状态 immediate UI update
		setItems((prev) => prev?.map((it) => it.message_id === m.message_id ? { ...it, stat: '1' } : it) ?? prev);
		try {
			await callInternal({
				short_name: 'message',
				action: '/user/message/updateState',
				method: 'POST',
				body: { message_id: m.message_id, sub_type: '1', widget_id: m.widget_id ?? '' },
				av: '2',
			});
		} catch {
			// 静默失败
		}
	}, []);

	const onSaveOrder = (next: string[]) => {
		setOrder(next);
		localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
		setEditorOpen(false);
		// 如果当前 active 被移除，回退到第一项
		if (!next.includes(active)) setActive(next[0] ?? visibleOrder[0] ?? DEFAULT_ORDER[0]);
		message.success(t('msg.saved'));
	};

	const actionItems = [
		{
			key: 'event',
			title: t('root.incidents.action_event'),
			subtitle: t('root.incidents.action_event_hint'),
			icon: <BellOutlined />,
			value: pendingEvents,
			badgeTone: pendingEvents && pendingEvents > 0 ? 'orange' : 'default',
		},
		{
			key: 'alert',
			title: t('root.incidents.action_alert'),
			subtitle: t('root.incidents.action_alert_hint'),
			icon: <AlertOutlined />,
			value: alarmUnread,
			badgeTone: alarmUnread > 0 ? 'red' : 'default',
		},
		{
			key: 'message',
			title: t('root.incidents.action_message'),
			subtitle: t('root.incidents.action_message_hint'),
			icon: <MailOutlined />,
			value: totalUnread,
			badgeTone: totalUnread > 0 ? 'blue' : 'default',
		},
	].reduce<ActionTileItem[]>((items, item) => {
		const cap = capByKey.get(item.key);
		if (!cap) return items;
		items.push({
			key: item.key,
			title: item.title,
			subtitle: item.subtitle,
			icon: item.icon,
			badge: <Tag color={item.badgeTone}>{metric(item.value)}</Tag>,
			onClick: () => navigate(cap.route),
		});
		return items;
	}, []);

	return (
		<MobileRootPage>
			<RootHeader
				icon={<BellOutlined />}
				title={t('tabbar.incidents')}
				subtitle={t('root.incidents.subtitle')}
				meta={<RegionTag region={region} />}
				action={
					<Button
						type="text"
						aria-label={t('root.incidents.refresh')}
						icon={<ReloadOutlined style={{ color: PRIMARY_TEXT }} />}
						onClick={() => void loadOverview()}
						loading={overviewLoading}
						style={{ minWidth: 44, minHeight: 44 }}
					/>
				}
			/>

			<SummaryStrip
				items={[
					{
						key: 'pending',
						label: t('root.incidents.pending'),
						value: metric(pendingEvents),
						hint: t('root.incidents.pending_hint'),
						tone: pendingEvents && pendingEvents > 0 ? 'warning' : 'default',
					},
					{
						key: 'today',
						label: t('root.incidents.today'),
						value: metric(todayEvents),
						hint: t('root.incidents.today_hint'),
					},
					{
						key: 'alerts',
						label: t('root.incidents.alerts'),
						value: metric(alarmUnread),
						hint: t('root.incidents.alerts_hint'),
						tone: alarmUnread > 0 ? 'danger' : 'default',
					},
					{
						key: 'unread',
						label: t('root.incidents.unread'),
						value: metric(totalUnread),
						hint: lastUpdatedLabel,
						tone: totalUnread > 0 ? 'warning' : 'default',
					},
				]}
			/>

			{overviewError && (
				<Alert
					type="warning"
					showIcon
					message={t('root.incidents.partial_data')}
					description={overviewError}
					style={{ marginBottom: 16 }}
				/>
			)}

			<SectionBlock title={t('root.incidents.workflow')}>
				<ActionTileGrid items={actionItems} min={184} />
			</SectionBlock>

			<SectionBlock title={t('root.incidents.status_overview')}>
				<div
					style={{
						display: 'grid',
						gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
						gap: 10,
					}}
				>
					<StatusCard label={t('event_count.wait')} value={metric(eventCounts ? countValue(eventCounts.wait) : null)} />
					<StatusCard label={t('event_count.wait_handle')} value={metric(pendingEvents)} tone="warning" />
					<StatusCard label={t('event_count.destroy')} value={metric(handledEvents)} tone="success" />
					<StatusCard label={t('root.incidents.visible_types')} value={formatNumber(visibleOrder.length, i18n.language)} />
				</div>
			</SectionBlock>

			<SectionBlock
				title={t('root.incidents.recent_messages')}
				extra={
					<Button
						type="text"
						size="small"
						aria-label={t('msg.order_title')}
						title={t('msg.order_title')}
						icon={<SettingOutlined />}
						onClick={() => setEditorOpen(true)}
					/>
				}
			>

			{/* 横向滚动 tabs（按 order） — role=tablist */}
			<div style={{
				marginTop: 8,
				overflowX: 'auto',
				WebkitOverflowScrolling: 'touch',
				borderBottom: `1px solid ${colors.graphic.divider}`,
			}}>
				<div role="tablist" aria-label={t('msg.order_title')} style={{ display: 'inline-flex', padding: '0 12px', whiteSpace: 'nowrap' }}>
					{visibleOrder.map((key) => {
						const meta = TYPE_BY_KEY.get(key);
						if (!meta) return null;
						const isActive = key === active;
						return (
							<button
								key={key}
								type="button"
								role="tab"
								aria-selected={isActive}
								tabIndex={isActive ? 0 : -1}
								onClick={() => setActive(key)}
								className="a11y-button"
								style={{
									padding: '12px 14px',
									minHeight: 44,
									fontSize: 14,
									fontWeight: isActive ? 600 : 400,
									color: isActive ? PRIMARY_TEXT : colors.text.secondary,
									borderBottom: isActive ? `2px solid ${PRIMARY_TEXT}` : '2px solid transparent',
									cursor: 'pointer',
									userSelect: 'none',
									width: 'auto',
								}}
							>
								{t(meta.i18nKey)}
								{unreadByType.get(key) ? (
				<span style={{ marginInlineStart: 6, color: colors.status.dangerText, fontWeight: 600 }}>
										{formatNumber(unreadByType.get(key) ?? 0, i18n.language)}
									</span>
								) : null}
							</button>
						);
					})}
				</div>
			</div>
			</SectionBlock>

			{listError && (
				<Alert
					type="warning"
					showIcon
					message={t('root.incidents.message_load_failed')}
					description={listError}
					style={{ marginBottom: 12 }}
				/>
			)}

			<div style={{
				display: 'grid',
				gridTemplateColumns: 'repeat(auto-fill, minmax(min(380px, 100%), 1fr))',
				gap: 10,
			}}>
				{loading && <div style={{ textAlign: 'center', padding: 40, gridColumn: '1 / -1' }}><Spin /></div>}
				{!loading && items?.length === 0 && (
					<Empty
						image={<MailOutlined style={{ fontSize: 80, color: '#bfbfbf' }} />}
						description={<Typography.Text type="secondary">{t('root.incidents.message_empty', { count: activeUnread })}</Typography.Text>}
						style={{ marginTop: 80, gridColumn: '1 / -1' }}
					/>
				)}
				{!loading && items && items.length > 0 && items.map((m) => (
					<MessageRow key={m.message_id} m={m} onRead={markRead} />
				))}
				{/* 触底哨兵 */}
				{!loading && items && items.length > 0 && (
					<div ref={sentinelRef} style={{ height: 1, gridColumn: '1 / -1' }} />
				)}
				{loadingMore && (
					<div role="status" aria-live="polite" style={{ textAlign: 'center', padding: 16, color: colors.text.secondary, fontSize: 12, gridColumn: '1 / -1' }}>
						<Spin size="small" /> <span style={{ marginInlineStart: 8 }}>{t('common.loading')}</span>
					</div>
				)}
				{!loading && !loadingMore && !hasMore && items && items.length > 0 && (
					<div style={{ textAlign: 'center', padding: 16, color: colors.text.tertiary, fontSize: 12, gridColumn: '1 / -1' }}>
						{t('msg.no_more')}
					</div>
				)}
			</div>

			<MsgOrderDrawer
				open={editorOpen}
				current={visibleOrder}
				availableTypes={allowedTypes}
				onClose={() => setEditorOpen(false)}
				onSave={onSaveOrder}
			/>
		</MobileRootPage>
	);
}

function StatusCard({
	label,
	value,
	tone = 'default',
}: {
	label: string;
	value: string | number;
	tone?: 'default' | 'success' | 'warning';
}) {
	const color = tone === 'success'
		? colors.status.successText
		: tone === 'warning'
			? colors.status.warningText
			: colors.text.primary;
	return (
		<div
			style={{
				background: colors.bg.card,
				border: `1px solid ${colors.graphic.divider}`,
				borderRadius: 8,
				padding: '12px 14px',
				minHeight: 74,
			}}
		>
			<div style={{ color: colors.text.secondary, fontSize: 12, lineHeight: 1.3 }}>{label}</div>
			<div style={{ color, fontSize: 20, fontWeight: 600, marginTop: 8, lineHeight: 1 }}>
				{value}
			</div>
		</div>
	);
}

/* ========== 消息类型排序 Drawer ========== */

function MsgOrderDrawer({
	open,
	current,
	onClose,
	onSave,
	availableTypes,
}: {
	open: boolean;
	current: string[];
	availableTypes: MsgType[];
	onClose: () => void;
	onSave: (next: string[]) => void;
}) {
	const { t } = useTranslation();
	const [draft, setDraft] = useState<string[]>(current);
	const [dragIdx, setDragIdx] = useState<number | null>(null);

	useEffect(() => { if (open) setDraft(current); }, [open, current]);

	const remove = (key: string) => setDraft((p) => p.filter((k) => k !== key));
	const add = (key: string) => setDraft((p) => p.includes(key) ? p : [...p, key]);
	const onDrop = (toIdx: number) => {
		if (dragIdx === null || dragIdx === toIdx) return;
		setDraft((p) => {
			const next = [...p];
			const [moved] = next.splice(dragIdx, 1);
			next.splice(toIdx, 0, moved);
			return next;
		});
		setDragIdx(null);
	};
	/** 键盘可达的相邻交换（替代鼠标 drag-drop） */
	const swap = (idx: number, dir: -1 | 1) => {
		const j = idx + dir;
		setDraft((p) => {
			if (j < 0 || j >= p.length) return p;
			const next = [...p];
			[next[idx], next[j]] = [next[j], next[idx]];
			return next;
		});
	};

	const draftSet = useMemo(() => new Set(draft), [draft]);
	const candidates = availableTypes.filter((m) => !draftSet.has(m.key));

	return (
		<Drawer
			placement="bottom"
			height="92%"
			open={open}
			onClose={onClose}
			closable={false}
			title={
				<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
					<Button type="text" onClick={onClose} style={{ color: colors.text.secondary, minHeight: 44, minWidth: 44 }}>
						{t('common.cancel')}
					</Button>
					<span style={{ flex: 1, textAlign: 'center', fontSize: 16, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
						{t('msg.order_title')}
					</span>
					<Button
						type="primary"
						onClick={() => onSave(draft)}
						style={{ background: PRIMARY, borderColor: PRIMARY, minHeight: 44 }}
					>
						{t('msg.save')}
					</Button>
				</div>
			}
			styles={{ body: { padding: 0, background: colors.bg.soft }, header: { padding: '8px 16px' } }}
		>
			<div role="listbox" aria-label={t('msg.order_title')} aria-orientation="vertical" style={{ background: colors.bg.card }}>
				{draft.map((key, idx) => {
					const meta = TYPE_BY_KEY.get(key);
					if (!meta) return null;
					const label = t(meta.i18nKey);
					return (
						<div
							key={key}
							role="option"
							aria-selected="true"
							aria-label={t('msg.row_label', { label, position: idx + 1, total: draft.length })}
							draggable
							onDragStart={() => setDragIdx(idx)}
							onDragOver={(e) => e.preventDefault()}
							onDrop={() => onDrop(idx)}
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: 8,
								padding: '8px 16px',
								minHeight: 56,
								borderBottom: `1px solid ${colors.graphic.divider}`,
							}}
						>
							{/* 移除（红 ⊖） */}
							<button
								type="button"
								className="a11y-button"
								onClick={(e) => { e.stopPropagation(); remove(key); }}
								aria-label={t('msg.remove_label', { label })}
								style={{
									width: 44,
									height: 44,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: colors.status.dangerText,
								}}
							>
								<MinusCircleOutlined style={{ fontSize: 20 }} />
							</button>
							<span style={{ flex: 1, fontSize: 15, color: colors.text.primary }}>{label}</span>
							{/* 上移 */}
							<button
								type="button"
								className="a11y-button"
								onClick={() => swap(idx, -1)}
								disabled={idx === 0}
								aria-label={t('msg.move_up_label', { label })}
								aria-disabled={idx === 0}
								style={{
									width: 44,
									height: 44,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: idx === 0 ? colors.text.disabled : colors.text.secondary,
								}}
							>
								<ArrowUpOutlined style={{ fontSize: 16 }} />
							</button>
							{/* 下移 */}
							<button
								type="button"
								className="a11y-button"
								onClick={() => swap(idx, 1)}
								disabled={idx === draft.length - 1}
								aria-label={t('msg.move_down_label', { label })}
								aria-disabled={idx === draft.length - 1}
								style={{
									width: 44,
									height: 44,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									color: idx === draft.length - 1 ? colors.text.disabled : colors.text.secondary,
								}}
							>
								<ArrowDownOutlined style={{ fontSize: 16 }} />
							</button>
							{/* 视觉拖拽手柄（仅鼠标用户可见暗示） */}
							<HolderOutlined aria-hidden="true" style={{ fontSize: 14, color: colors.text.tertiary, cursor: 'move' }} />
						</div>
					);
				})}
			</div>

			{candidates.length > 0 && (
				<>
					<h3 style={{
						padding: '14px 16px 6px',
						margin: 0,
						color: colors.text.secondary,
						fontSize: 12,
						fontWeight: 500,
						background: colors.graphic.divider,
					}}>
						{t('msg.available')}
					</h3>
					<div role="list" style={{ background: colors.bg.card }}>
						{candidates.map((m) => {
							const label = t(m.i18nKey);
							return (
								<button
									key={m.key}
									type="button"
									className="a11y-button"
									onClick={() => add(m.key)}
									aria-label={t('msg.add_label', { label })}
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: 14,
										padding: '14px 16px',
										minHeight: 56,
										borderBottom: `1px solid ${colors.graphic.divider}`,
										cursor: 'pointer',
									}}
								>
									<PlusCircleOutlined aria-hidden="true" style={{ fontSize: 20, color: colors.status.successText }} />
									<span style={{ flex: 1, fontSize: 15, textAlign: 'start', color: colors.text.primary }}>{label}</span>
								</button>
							);
						})}
					</div>
				</>
			)}
		</Drawer>
	);
}

/* ========== 单条消息行（按 type 分发） ========== */

function MessageRow({
	m,
	onRead,
}: {
	m: MessageItemInfo;
	onRead: (m: MessageItemInfo) => void;
}) {
	// 重点人员到店：用专门卡片样式（结构化字段 + 大缩略图）
	// 反编译参考：MessageKeyPersonAdapter
	if (m.relation && (m.relation.realname || m.relation.arrive_time || m.relation.arrive_imgurl)) {
		return <KeyPersonRow m={m} onRead={onRead} />;
	}
	// 悠掌柜（POS 交易）：16:9 头图 + 类型 icon + title 解析
	// 反编译参考：MessageShopKeeperAdapter
	if (m.relation && (m.relation.trade_time || m.relation.img_url || m.relation.type)) {
		return <PosTradeRow m={m} onRead={onRead} />;
	}
	return <GenericRow m={m} onRead={onRead} />;
}

function GenericRow({
	m,
	onRead,
}: {
	m: MessageItemInfo;
	onRead: (m: MessageItemInfo) => void;
}) {
	const { t } = useTranslation();
	const unread = m.stat === '0';
	const text = m.format_text || stripHtml(m.text);
	// 缩略图优先级：images[0] → relation.arrive_imgurl（重点人员到店）
	const thumb = (m.images && m.images[0]) || m.relation?.arrive_imgurl;
	const altText = `${m.title || ''} ${m.create_time || ''}`.trim();
	return (
		<button
			type="button"
			className="a11y-button"
			onClick={() => unread && onRead(m)}
			disabled={!unread}
			aria-disabled={!unread}
			aria-label={unread ? t('msg.unread_card_label', { title: m.title || '—' }) : undefined}
			style={{
				display: 'flex',
				gap: 12,
				padding: 12,
				marginBottom: 8,
				borderRadius: 8,
				background: unread ? colors.primary.soft : colors.bg.card,
				border: `1px solid ${unread ? colors.primary.softBorder : colors.graphic.divider}`,
				cursor: unread ? 'pointer' : 'default',
				position: 'relative',
				width: '100%',
				textAlign: 'start',
			}}
		>
			{thumb ? (
				<img
					src={thumb}
					alt={altText}
					onError={(e) => {
						(e.currentTarget as HTMLImageElement).style.display = 'none';
					}}
					style={{
						width: 60,
						height: 60,
						objectFit: 'cover',
						borderRadius: 6,
						flex: '0 0 60px',
						background: colors.bg.page,
					}}
				/>
			) : null}
			<div style={{ flex: 1, minWidth: 0 }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
					<Typography.Text
						strong
						style={{ fontSize: 14, color: unread ? colors.text.primary : colors.text.secondary }}
					>
						{m.title || '—'}
					</Typography.Text>
					{unread && (
						<>
							<span aria-hidden="true"
								style={{
									width: 8,
									height: 8,
									borderRadius: '50%',
									background: colors.status.dangerSolid,
									display: 'inline-block',
								}}
							/>
							<span className="sr-only">{t('msg.status.unread')}</span>
						</>
					)}
				</div>
				<Typography.Paragraph
					ellipsis={{ rows: 2 }}
					style={{
						margin: '0 0 4px',
						color: unread ? colors.text.primary : colors.text.secondary,
						fontSize: 13,
						lineHeight: 1.5,
					}}
				>
					{text || '—'}
				</Typography.Paragraph>
				<Typography.Text style={{ fontSize: 11, color: colors.text.tertiary }}>
					{m.create_time}
				</Typography.Text>
			</div>
		</button>
	);
}

/* ========== 重点人员到店专属卡片 ========== */

function KeyPersonRow({
	m,
	onRead,
}: {
	m: MessageItemInfo;
	onRead: (m: MessageItemInfo) => void;
}) {
	const { t } = useTranslation();
	const unread = m.stat === '0';
	const r = m.relation ?? {};
	const arriveTime = r.arrive_time || m.create_time;
	const storeName = r.group_name || r.store_name || '';
	const realname = r.realname || '';
	const sexLabel = r.sex === '1' ? t('msg.kp.male') : r.sex === '0' ? t('msg.kp.female') : '';
	const arriveNum = r.arrive_num || '';
	const thumb = r.arrive_imgurl;

	const cardLabel = `${t('msg_t.key_person')} · ${realname || ''} · ${arriveTime || ''}`.trim();
	return (
		<button
			type="button"
			className="a11y-button"
			onClick={() => unread && onRead(m)}
			disabled={!unread}
			aria-disabled={!unread}
			aria-label={cardLabel}
			style={{
				display: 'flex',
				gap: 12,
				padding: 14,
				marginBottom: 10,
				borderRadius: 10,
				background: unread ? colors.primary.soft : colors.bg.card,
				border: `1px solid ${unread ? colors.primary.softBorder : colors.graphic.divider}`,
				cursor: unread ? 'pointer' : 'default',
				alignItems: 'flex-start',
				width: '100%',
				textAlign: 'start',
			}}
		>
			{/* 大圆角缩略图（左） */}
			<div
				style={{
					width: 96,
					height: 96,
					borderRadius: 8,
					overflow: 'hidden',
					background: colors.bg.soft,
					flex: '0 0 96px',
					position: 'relative',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				{/* 占位（z-index 0），图片成功加载会盖在上面 */}
				<div
					aria-hidden="true"
					style={{
						color: colors.text.tertiary,
							fontSize: 20,
						lineHeight: 1,
					}}
				>
					👤
				</div>
				{thumb ? (
					<img
						src={thumb}
						alt={`${realname} ${arriveTime}`}
						onError={(e) => {
							(e.currentTarget as HTMLImageElement).style.display = 'none';
						}}
						style={{
							position: 'absolute',
							inset: 0,
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
				) : null}
			</div>

			{/* 结构化字段（右） — 视觉层级：姓名/到店次数最强，门店/时间中，性别弱 */}
			<div style={{ flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.65 }}>
				{/* 姓名（最高优先级，加粗 14px） */}
				<div style={{ display: 'flex', gap: 8, marginBottom: 2 }}>
					<span style={{ color: colors.text.secondary, whiteSpace: 'nowrap', fontSize: 12 }}>
						{t('msg.kp.name')}:
					</span>
					<span style={{
						color: unread ? colors.text.primary : colors.text.secondary,
							fontWeight: 600,
						fontSize: 14,
						wordBreak: 'break-all',
					}}>
						{realname || '—'}
					</span>
				</div>
				<KvRow label={t('msg.kp.arrive_time')} value={arriveTime} unread={unread} />
				<KvRow label={t('msg.kp.store')} value={storeName} unread={unread} />
				<KvRow label={t('msg.kp.sex')} value={sexLabel} unread={unread} muted />
				{/* 到店次数（最强调色，与姓名并列 hierarchy） */}
				<div style={{ display: 'flex', gap: 8, marginTop: 2 }}>
					<span style={{ color: colors.text.secondary, whiteSpace: 'nowrap', fontSize: 12 }}>
						{t('msg.kp.record')}:
					</span>
					{arriveNum ? (
						<span style={{ color: colors.primary.text, fontWeight: 600, fontSize: 14 }}>
							{t('msg.kp.arrive_n_times', { n: arriveNum })}
						</span>
					) : (
						<span style={{ color: colors.text.tertiary }}>—</span>
					)}
				</div>
			</div>

			{unread && (
				<>
					<span aria-hidden="true"
						style={{
							width: 8,
							height: 8,
							borderRadius: '50%',
							background: colors.status.dangerSolid,
							flex: '0 0 8px',
							marginTop: 4,
						}}
					/>
					<span className="sr-only">{t('msg.status.unread')}</span>
				</>
			)}
		</button>
	);
}

function KvRow({ label, value, unread, muted }: { label: string; value: string; unread: boolean; muted?: boolean }) {
	return (
		<div style={{ display: 'flex', gap: 8 }}>
			<span style={{ color: colors.text.secondary, whiteSpace: 'nowrap', fontSize: 12 }}>{label}:</span>
			<span
				style={{
					color: muted
						? colors.text.tertiary
						: unread
						? colors.text.primary
						: colors.text.secondary,
					wordBreak: 'break-all',
					fontSize: 12,
				}}
			>
				{value || '—'}
			</span>
		</div>
	);
}

/* ========== 悠掌柜 POS 交易专属卡片 ========== */
// 反编译参考：MessageShopKeeperAdapter.getPictureView()
//   - 16:9 头图（thumbnail_rl 高度按 (screenWidth-20)*9/16）
//   - relation.trade_time / store_name / title (含 "：" 分隔的 prefix + ";" 多值)
//   - relation.type "1"~"15" 不同 icon

/** POS 交易类型 icon emoji 映射（反编译 1-15 类） */
const POS_TYPE_EMOJI: Record<string, string> = {
	'1': '🛒', // 销售
	'2': '↩️', // 退款
	'3': '💳', // 充值
	'4': '📉', // 报损
	'5': '✂️', // 抹零
	'6': '🤖', // AI 异常
	'7': '🔧', // 修正
	'8': '📦', // 退单退货
	'9': '🔁', // 反结账
	'10': '🏷️', // 打折单
	'11': '🛵', // 外卖退单
	'12': '🚫', // 拒绝订单
	'13': '⚠️', // 异常取消
	'14': '⚠️', // 异常退单
	'15': '🆓', // 免单
};

function PosTradeRow({
	m,
	onRead,
}: {
	m: MessageItemInfo;
	onRead: (m: MessageItemInfo) => void;
}) {
	const { t } = useTranslation();
	const unread = m.stat === '0';
	const r = m.relation ?? {};
	const tradeTime = r.trade_time || m.create_time;
	const storeName = r.store_name || r.group_name || '';
	const thumb = r.img_url;
	const isAI = r.type === '6';
	const emoji = (r.type && POS_TYPE_EMOJI[r.type]) || '📋';
	const typeLabel = r.type ? t(`msg.pos.type_${r.type}`, { defaultValue: '' }) : '';

	// 解析 title：prefix（"："前）+ values（";" 分隔多行）
	const { prefix, lines } = parsePosTitle(r.title || '');

	const cardLabel = `${typeLabel || t('msg.pos.fallback')} · ${storeName} · ${tradeTime}`.trim();
	return (
		<button
			type="button"
			className="a11y-button"
			onClick={() => unread && onRead(m)}
			disabled={!unread}
			aria-disabled={!unread}
			aria-label={cardLabel}
			style={{
				marginBottom: 10,
				borderRadius: 10,
				overflow: 'hidden',
				background: unread ? colors.primary.soft : colors.bg.card,
				border: `1px solid ${unread ? colors.primary.softBorder : colors.graphic.divider}`,
				cursor: unread ? 'pointer' : 'default',
				width: '100%',
				textAlign: 'start',
			}}
		>
			{/* 16:9 头图 */}
			{thumb ? (
				<div
					style={{
						width: '100%',
						aspectRatio: '16 / 9',
						background: colors.graphic.divider,
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					<img
						src={thumb}
						alt={cardLabel}
						onError={(e) => {
							(e.currentTarget as HTMLImageElement).style.display = 'none';
						}}
						style={{ width: '100%', height: '100%', objectFit: 'cover' }}
					/>
					{isAI && (
						<span
							aria-label={t('msg.pos.type_6')}
							style={{
								position: 'absolute',
								top: 8,
								insetInlineEnd: 8,
								background: colors.status.dangerSolid,
								color: '#fff',
								padding: '2px 8px',
								borderRadius: 4,
								fontSize: 11,
								fontWeight: 600,
							}}
						>
							AI
						</span>
					)}
				</div>
			) : null}

			{/* 文字区 */}
			<div style={{ padding: 12 }}>
				{/* 行 1: store + time + 未读点 */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						marginBottom: 6,
						fontSize: 12,
						color: colors.text.secondary,
					}}
				>
					<span style={{ fontWeight: 500, color: colors.text.primary }}>{storeName || '—'}</span>
					<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
						{tradeTime}
						{unread && (
							<>
								<span aria-hidden="true"
									style={{
										width: 8,
										height: 8,
										borderRadius: '50%',
										background: colors.status.dangerSolid,
										display: 'inline-block',
									}}
								/>
								<span className="sr-only">{t('msg.status.unread')}</span>
							</>
						)}
					</span>
				</div>

				{/* 行 2: emoji + type label + prefix */}
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: 6,
						marginBottom: 4,
					}}
				>
					<span aria-hidden="true" style={{ fontSize: 16 }}>
						{emoji}
					</span>
					<span style={{ fontSize: 14, fontWeight: 500, color: unread ? colors.text.primary : colors.text.secondary }}>
						{typeLabel || prefix.replace(/[:：]\s*$/, '') || t('msg.pos.fallback')}
					</span>
				</div>

				{/* 行 3+: values 多行展示 */}
				{lines.length > 0 && (
					<div style={{ paddingInlineStart: 22, color: unread ? colors.text.primary : colors.text.secondary, fontSize: 13, lineHeight: 1.6 }}>
						{lines.map((line, i) => (
							<div key={i} style={{ wordBreak: 'break-all' }}>
								{line}
							</div>
						))}
					</div>
				)}
			</div>
		</button>
	);
}

/**
 * 解析 POS title 格式：
 *   "状态：异常营业;异常时长 5 分钟" → { prefix: "状态：", lines: ["异常营业", "异常时长 5 分钟"] }
 *   "退款金额：100 元"             → { prefix: "退款金额：", lines: ["100 元"] }
 *   "无：分隔的字符串"              → { prefix: "", lines: ["无：分隔的字符串"] }
 *
 * 反编译：
 *   int iIndexOf = title.indexOf("：") + 1;
 *   String prefix = title.substring(0, iIndexOf);
 *   if (title.indexOf(";") >= 0) split by ";" → multi-line
 */
function parsePosTitle(title: string): { prefix: string; lines: string[] } {
	if (!title) return { prefix: '', lines: [] };
	const colonIdx = title.indexOf('：');
	if (colonIdx < 0) return { prefix: '', lines: [title] };
	const prefix = title.substring(0, colonIdx + 1);
	const rest = title.substring(colonIdx + 1);
	const lines = rest.includes(';') ? rest.split(';').map((s) => s.trim()).filter(Boolean) : [rest];
	return { prefix, lines };
}

/** 富文本消息（含 <a>...</a> 标签）抽纯文本 */
function stripHtml(s: string): string {
	if (!s) return '';
	return s.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').trim();
}
